import { Injectable } from '@angular/core';
import { AssetInfoModel, CollectionBlobInfoModel, indexedDbStore, OfflineService, PhdsoftCommonService, RegionItem, Util } from '@phdsoft/common';
import { AnomalyModel } from 'projects/phdsoft/common/src/lib/models/anomaly.model';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { AnomalyService } from 'src/app/admin/services/anomaly.service';
import { InspectionPlanService } from 'src/app/inspection-plan/inspection-plan.service';
import { DigitalTwinService } from 'src/app/digital-twin/digital-twin.service';
import { NotificationService } from 'src/app/signalr/notification.service';
import { DocumentsListService } from 'src/app/template/documents/documents-list/documents-list.service';
import { CurrentAsset } from '../models/current-asset';
import { AssetService } from './asset.service';
import { RepairService } from './repair.service';
import { ElementInsertionService } from 'src/app/admin/services/element-insertion.service';
import { CrudBasicDomainsService } from 'src/app/admin/services/crud-basic-domains.service';
import { OfflinePhotoService } from './media-services/offline-photo.service';
import { IInspectionPlan } from '@app/inspection-plan/inspection-plan.interface';
import { FilterService } from 'src/app/filter/filter.service';
import { v4 as uuid } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class AssetOfflineService {

  public asset: CurrentAsset;
  public assetId: string;
  private promiseList: Promise<void>[] = [];

  constructor(
    private readonly offlineService: OfflineService,
    private readonly phdsoftCommonService: PhdsoftCommonService,
    private readonly anomalyService: AnomalyService,
    private readonly repairService: RepairService,
    private readonly inpectionPlanService: InspectionPlanService,
    private readonly digitalTwinService: DigitalTwinService,
    private readonly crudService: CrudBasicDomainsService,
    private readonly assetService: AssetService,
    private readonly notificationService: NotificationService,
    private readonly documentListService: DocumentsListService,
    private readonly elementInsertionService: ElementInsertionService,
    private readonly offlinePhotoService: OfflinePhotoService,
    private readonly filterService: FilterService
  ) { }

  async assetOffline(assetId: string): Promise<CurrentAsset> {
    const offline: indexedDbStore = await this.offlineService.getItem(assetId);

    // If offline get offline resources
    if (!Util.isNullOrUndefined(offline)) {
      this.asset = Util.isJson(offline.json) ? JSON.parse(offline.json) : offline.json;
      this.assetId = this.asset.AssetId;
    }

    return this.asset;
  }

  async assetSavedOffline(assetId: string): Promise<boolean> {
    const currentAsset = await this.assetOffline(assetId);
    return !Util.isNullOrUndefined(currentAsset);
  }

  async getAssetOfflineRegionIds(assetId: string, regionIds: string[]): Promise<string[]> {
    // Use Promise.all to wait for all checks to complete
    const results = await Promise.all(
      regionIds.map(async (regionId) => {
        const offlineId = `${assetId}_##_${regionId}`;
        const offlineRegionData = await this.offlineService.getItem(offlineId);

        // Return either the id or null
        return !Util.isNullOrUndefined(offlineRegionData) ? regionId : null;
      })
    );

    // Filter out null values
    return results.filter((id): id is string => id !== null);
  }

  async assetSaveRegionOffline(
    assetInfoModel: AssetInfoModel,
    regionId: string,
    regionItems: RegionItem[],
    addOffline: boolean,
  ): Promise<boolean> {
    const title: string = "Region save/update ";
    this.notificationService.addInProgressNotification(title)

    const offlineId = `${assetInfoModel.AssetId}_##_${regionId}`;
    const offlineRegions = regionItems
      .filter((r) => {
        if (r.offline === true) {
          return r.id;
        }
      })
      .flatMap((r) => r.id);

    const copyAssetInfoModel: AssetInfoModel = JSON.parse(JSON.stringify(assetInfoModel));
    copyAssetInfoModel.Regions = copyAssetInfoModel.Regions.filter((r) =>
      offlineRegions.includes(r.id),
    );

    // Filter all Collections from RegionId
    copyAssetInfoModel.Collections = copyAssetInfoModel.Collections.filter(async (c) => {
      c.BlobPaths = c.BlobPaths.filter((bp) => bp.RegionId === regionId);
      return c;
    });

    // After filter Selected Region
    // download all Collections BlobPaths
    const forLoopStructure = async () => {
      let structureCol = copyAssetInfoModel.Collections.filter(c => c.Type === 'b0675e9cbaf244cda6199cb460a2bacd');
      for (const col of structureCol) {
        // Download GLTF and all BlobPàths from Structure Collection
        await this.addOrRemoveBlobPaths(col.BlobPaths, addOffline);
      }
      structureCol = null;
    };

    // Await all Downloads
    await forLoopStructure();
    console.log('Downloaded blob paths')
    // Filter all Region sections
    copyAssetInfoModel.Sections = copyAssetInfoModel.Sections.filter((s) =>
      s.regions.filter((srid) => offlineRegions.includes(srid)),
    );
    console.log('Filtered region sections')
    // Filter all region Templates
    copyAssetInfoModel.Templates = copyAssetInfoModel.Templates.filter((t) =>
      offlineRegions.includes(t.RegionId),
    );
    console.log('Filtered region')

    let key = null;

    if (addOffline) {
      await Promise.all([
        this.saveLightModel(assetInfoModel.LightModelPath),
        this.saveAssetInfo(assetInfoModel.AssetId),
        this.saveAnomaliesOnline(assetInfoModel.AssetId),
        this.saveRepairs(assetInfoModel.AssetId, regionId),
        this.saveLightSectionMods(assetInfoModel.AssetId, regionId),
        this.saveCurrentInspectionPlan(assetInfoModel.AssetId, regionId),
        this.saveHistoricalInspectionPlans(assetInfoModel.AssetId, regionId),
        this.saveFilters(assetInfoModel.AssetId, regionId),
        this.saveElementsFromRegion(assetInfoModel.AssetId, regionId),
        this.saveInspectionTypeList(assetInfoModel.InspectionList, assetInfoModel.AssetId),
        this.saveDocuments(assetInfoModel.AssetId)
      ]);
      // TODO: inspection photos?

      key = await this.offlineService.storeAsync(offlineId, true);
    } else {
      await this.offlineService.removeItem(offlineId);
      const hasMoreOfflineRegions = await this.hasMoreOfflineRegions(assetInfoModel.AssetId);

      await Promise.all([
        this.removeLightModel(assetInfoModel.LightModelPath, hasMoreOfflineRegions),
        this.removeAssetInfo(assetInfoModel.AssetId, hasMoreOfflineRegions),
        this.removeAnomaliesOffline(assetInfoModel.AssetId, hasMoreOfflineRegions),
        this.removeRepairs(assetInfoModel.AssetId, regionId),
        this.removeLightSectionMods(assetInfoModel.AssetId, regionId),
        this.removePlansFromRegion(assetInfoModel.AssetId, regionId),
        this.removeHistoricalPlansFromRegion(assetInfoModel.AssetId, regionId),
        this.removeFiltersFromRegion(assetInfoModel.AssetId, regionId),
        this.removeElementsFromRegion(assetInfoModel.AssetId, regionId),
        this.removeInspectionTypeList(assetInfoModel.AssetId, hasMoreOfflineRegions),
        this.removeDocuments(assetInfoModel.AssetId, hasMoreOfflineRegions),
        this.offlinePhotoService.clearOfflinePhotos(assetInfoModel.AssetId, regionId)
      ])
    }


    this.notificationService.addSuccessNotification(title)

    return Promise.resolve(addOffline ? key !== null : true);
  }

  /**
   * Adds or removes blob paths based on the value of addOffline.
   * @param blobPaths - The collection of blob paths.
   * @param addOffline - A boolean indicating whether to add the blob paths offline or remove them.
   * @returns A promise that resolves when the operation is complete.
   */
  async addOrRemoveBlobPaths(blobPaths: CollectionBlobInfoModel[], addOffline: boolean) {
    const blobPathsStr: string[] = this.getBlobPaths(blobPaths);

    if (addOffline) {
      await this.offlineAddBlobPaths(blobPathsStr);
    } else {
      await this.offlineRemoveBlobPaths(blobPathsStr);
    }
  }

  /**
   * Retrieves an array of blob paths from the given collection of blob information models.
   * @param blobPaths - The collection of blob information models.
   * @returns An array of blob paths.
   */
  getBlobPaths(blobPaths: CollectionBlobInfoModel[]): string[] {
    return blobPaths.reduce((paths, path) => {
      ['FEAPath', 'LinearElementsPath', 'PropertiesPath', 'RenderGeoPath'].forEach(key => {
        if (!Util.isNullOrUndefined(path[key])) {
          paths.push(path[key]);
        }
      });
      return paths;
    }, []);
  }

  async offlineRemoveBlobPaths(blobPaths: string[]) {
    for (const path of blobPaths) {
      const urlWithoutParams = path.substring(0, path.indexOf('?'));
      this.offlineService.removeItem(urlWithoutParams);
    }
  }

  //Add blobs to offline
  async offlineAddBlobPaths(blobPaths: string[]) {
    this.promiseList = [];

    const forLoop = async () => {
      for (const path of blobPaths) {
        // if (path.toLowerCase().indexOf('.gltf') > 0) {
        this.promiseList.push(this.phdsoftCommonService.getFileArrayBuffer(path, true, true));
        // } else {
        //   this.phdsoftCommonService.runBackground(
        //     this.phdsoftCommonService.getFile(path, true));
        // }
      }
    };
    await forLoop();
    return await Promise.all(this.promiseList);
  }

  async saveLightModel(lightModelPath: string) {
    if (lightModelPath) {
      console.log('Saving light model...')
      await this.phdsoftCommonService.getFileArrayBuffer(lightModelPath, true, true);
      console.log('Light model saved !')
    }
  }

  async removeLightModel(lightModelPath: string, hasMoreOfflineRegions: boolean) {
    if (!lightModelPath) {
      return;
    }

    if (!hasMoreOfflineRegions) {
      console.log('Removing light model...')
      const urlWithoutParams = lightModelPath.substring(0, lightModelPath.indexOf('?'));
      this.offlineService.removeItem(urlWithoutParams);
      console.log('Light model removed !')
    } else {
      console.log('Light model not removed ! There are other regions offline !')
    }
  }

  async saveAnomaliesOnline(assetId: string) {
    let anomalies = await this.anomalyService.getAnomalyList(assetId)
    await this.offlineService.storeAsync(`anomalies_${assetId}`, anomalies)
  }

  async saveAnomaliesOffline(anomalies: AnomalyModel[], assetId: string) {
    try {
      await this.offlineService.storeAsync(`anomalies_${assetId}`, anomalies)
    }
    catch (error) {
      console.error("Error saving anomalies offline !", error)
    }
  }

  async removeAnomaliesOffline(assetId: string, hasMoreOfflineRegions: boolean) {
    if (!hasMoreOfflineRegions) {
      console.log('Removing anomalies offline...')
      await this.offlineService.removeItem(`anomalies_${assetId}`);
      console.log('Anomalies offline removed !')
    } else {
      console.log('Anomalies offline not removed ! There are other regions offline !')
    }
  }

  async saveRepairs(assetId: string, regionId: string) {
    let repairs = await this.repairService.getRepairElements(assetId, regionId, new Date().toJSON())
    await this.offlineService.storeAsync(`repairs_${regionId}`, repairs)
  }

  async removeRepairs(assetId: string, regionId: string) {
    console.log('Removing repairs from region...')
    await this.offlineService.removeItem(`repairs_${regionId}`);
    console.log('Repairs from region removed !')
  }

  async saveLightSectionMods(assetId: string, regionId: string) {
    try {
      const mods = await this.repairService.getRegionLightSectionMods(assetId, regionId);
      await this.offlineService.storeAsync(`lightsectionmods_${assetId}_${regionId}`, mods);
    } catch (error) {
      console.error('Error saving lightsection mods offline:', error);
    }
  }

  async removeLightSectionMods(assetId: string, regionId: string) {
    await this.offlineService.removeItem(`lightsectionmods_${assetId}_${regionId}`);
  }

  /**
   * Saves the current inspection plan for a given asset and region.
   * @param assetId The ID of the asset.
   * @param regionId The ID of the region.
   * @returns A promise that resolves when the inspection plan is saved.
   */
  async saveCurrentInspectionPlan(assetId: string, regionId: string) {
    const data = { AssetId: assetId, RegionId: regionId, CurrentDate: new Date().toJSON() };
    try {
      const result = await this.inpectionPlanService.getCurrentInspectionPlanByRegionId(data).toPromise();
      if (result) {
        // Attention: more than one plan can be stored in inspectionPlan_assetId_regionId ; it is an array!
        await this.offlineService.storeAsync(`inspectionPlan_${assetId}_${regionId}`, result);
        for (const iterator of result) {
          await this.saveInspectionsFromPlan(iterator.id);
        }
      }
    } catch (error) {
      console.error('Error in saveCurrentInspectionPlan:', error);
    }
  }

  /**
   * Saves the historical inspection plans for a given asset and region.
   * @param assetId The ID of the asset.
   * @param regionId The ID of the region.
   * @returns A promise that resolves when the historical plans are saved.
   */
  async saveHistoricalInspectionPlans(assetId: string, regionId: string) {
    try {
      const result = await this.inpectionPlanService.getInspectionPlanByRegionId(assetId, [regionId]).toPromise();
      if (result) {
        await this.offlineService.storeAsync(`historicalPlans_${assetId}_${regionId}`, result);
        console.log(`Historical plans saved for asset ${assetId}, region ${regionId}`);
      }
    } catch (error) {
      console.error('Error in saveHistoricalInspectionPlans:', error);
    }
  }

  /**
   * Saves the filters for a given asset and region.
   * @param assetId The ID of the asset.
   * @param regionId The ID of the region.
   * @returns A promise that resolves when the filters are saved.
   */
  async saveFilters(assetId: string, regionId: string) {
    try {
      const result = await this.filterService.getFiltersByRegionIds(assetId, [regionId]).toPromise();
      if (result) {
        await this.offlineService.storeAsync(`filters_${assetId}_${regionId}`, result);
        console.log(`Filters saved for asset ${assetId}, region ${regionId}`);
      }
    } catch (error) {
      console.error('Error in saveFilters:', error);
    }
  }

  async removePlansFromRegion(assetId: string, regionId: string) {
    console.log('Removing plans from region...')
    const plans = await this.offlineService.getItem(`inspectionPlan_${assetId}_${regionId}`);
    if (plans) {
      for (const plan of plans.json) {
        await this.offlineService.removeItem(`inspections_inspectionPlan_${plan.id}`);
      }
    }
    await this.offlineService.removeItem(`inspectionPlan_${assetId}_${regionId}`);
    console.log('Plans from region removed !')
  }

  async removeHistoricalPlansFromRegion(assetId: string, regionId: string) {
    console.log('Removing historical plans from region...')
    await this.offlineService.removeItem(`historicalPlans_${assetId}_${regionId}`);
    console.log('Historical plans from region removed !')
  }

  async removeFiltersFromRegion(assetId: string, regionId: string) {
    console.log('Removing filters from region...')
    await this.offlineService.removeItem(`filters_${assetId}_${regionId}`);
    console.log('Filters from region removed !')
  }

  /**
   * Saves the elements for a given asset and region.
   * @param assetId The ID of the asset.
   * @param regionId The ID of the region.
   * @returns A promise that resolves when the elements are saved successfully, or rejects with an error if there was an issue.
   */
  async saveElementsFromRegion(assetId: string, regionId: string) {
    try {
      const data = await this.elementInsertionService.getAssetElements(assetId, regionId).toPromise();
      //console.log('Elements from region:', data);
      if (data) {
        try {
          await this.offlineService.storeAsync(`elements_region_${assetId}_${regionId}`, data);
          //console.log('Offline Elements from region:', data);
        } catch (error) {
          console.error('Error storing elements from region data:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching elements from region:', error);
    }
  }

  async removeElementsFromRegion(assetId: string, regionId: string) {
    console.log('Removing elements from region...')
    await this.offlineService.removeItem(`elements_region_${assetId}_${regionId}`);
    console.log('Elements from region removed !')
  }

  /**
   * Saves inspections from a plan to the offline storage.
   * @param planId The ID of the plan.
   * @returns A promise that resolves when the inspections are saved successfully, or rejects with an error if there was an issue.
   */
  async saveInspectionsFromPlan(planId: string) {
    try {
      const data = await this.digitalTwinService.getInspectionsByPlans(planId).toPromise();
      //console.log('Inspections from plan:', data);
      if (data) {
        try {
          await this.offlineService.storeAsync(`inspections_inspectionPlan_${planId}`, data);
          //console.log('Offline Inspections from plan:', data);
        } catch (error) {
          console.error('Error storing inspection plan data:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching inspections from plan:', error);
    }
  }

  /**
   * Saves the inspection type list for a given asset.
   * @param {Array<any>} inspectionTypes - The inspection types to be saved.
   * @param {string} assetId - The ID of the asset.
   * @returns {Promise<void>} - A promise that resolves when the inspection types are saved successfully.
   */
  async saveInspectionTypeList(inspectionTypes, assetId) {
    try {
      const data = await this.crudService.getCrudBasicDomainsInspectionList(inspectionTypes).toPromise();
      if (data) {
        try {
          await this.offlineService.storeAsync(`inspectionTypes_${assetId}`, data);
        } catch (error) {
          console.error('Error saving inspection type list:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching inspection type list:', error);
    }
  }

  async removeInspectionTypeList(assetId: string, hasMoreOfflineRegions: boolean) {
    if (!hasMoreOfflineRegions) {
      console.log('Removing inspection type list...')
      await this.offlineService.removeItem(`inspectionTypes_${assetId}`);
      console.log('Inspection type list removed !')
    } else {
      console.log('Inspection type list not removed ! There are other regions offline !')
    }
  }

  async hasMoreOfflineRegions(assetId: string) {
    const response = await this.offlineService.getItem(assetId);
    const _RESPONSE = response ? response.json : undefined;
    if (!Util.isNullOrUndefined(_RESPONSE)) {
      for (const region of _RESPONSE?.AssetInfo?.Regions || []) {
        const offlineId = `${_RESPONSE.AssetInfo.AssetId}_##_${region.id}`;
        const offlineInfo = await this.offlineService.getItem(offlineId);
        if (!Util.isNullOrUndefined(offlineInfo)) {
          return true;
        }
      }
    }
    return false;
  }

  async saveAssetInfo(assetId: string) {
    console.log('Saving asset info...')
    this.assetService.getAssetInfobyId(assetId, true)
    console.log('Asset Info saved !')
  }

  async removeAssetInfo(assetId: string, hasMoreOfflineRegions: boolean) {
    if (!hasMoreOfflineRegions) {
      console.log('Removing asset info...')
      await this.offlineService.removeItem(assetId);
      console.log('Asset info removed !')
    } else {
      console.log('Asset info not removed ! There are other regions offline !')
    }
  }

  async saveDocuments(assetId: string) {
    let documentsList = await this.documentListService.getAssetDocuments(assetId)
    if (documentsList) {
      await this.offlineService.storeAsync(`documentsList_${assetId}`, documentsList)
    }
  }

  async removeDocuments(assetId: string, hasMoreOfflineRegions: boolean) {
    if (!hasMoreOfflineRegions) {
      console.log('Removing documents...')
      await this.offlineService.removeItem(`documentsList_${assetId}`);
      console.log('Documents removed !')
    } else {
      console.log('Documents not removed ! There are other regions offline !')
    }
  }

  /**
   * Keeps the offline copy of saved inspections current, so an offline reload shows them.
   *
   * Each saved plan stores whole inspection documents, and one point can sit in several of them
   * (a periodic and an action plan of the same type). Every saved plan that already holds a point
   * gets the new copy: updating only one would leave a stale copy that wins the next reload
   * whenever its plan happens to load first. A point no saved plan holds goes into `planId` when
   * given (the plan it was written for), else the first saved plan of its type, else a temporary
   * plan created for it.
   */
  async saveInspectionToOffline(inspectionElements: ElementResultInfo[],
    assetId: string, regionId: string, referenceDate: Date, planId?: string
  ) {
    try {
      const plans = await this.getSavedPlans(assetId, regionId)
      const byId = new Map(inspectionElements.map(inspection => [inspection.id, inspection]))
      const stored = new Set<string>()

      for (const plan of plans) {
        const key = `inspections_inspectionPlan_${plan.id}`
        const offlineInspections = (await this.offlineService.getItem(key))?.json as ElementResultInfo[] | undefined
        if (!offlineInspections?.length) continue

        let changed = false
        offlineInspections.forEach((inspection, index) => {
          const updated = byId.get(inspection.id)
          if (updated) {
            offlineInspections[index] = updated
            stored.add(inspection.id)
            changed = true
          }
        })
        if (changed) {
          await this.offlineService.storeAsync(key, offlineInspections)
        }
      }

      const unstored = inspectionElements.filter(inspection => !stored.has(inspection.id))
      if (unstored.length === 0) {
        return
      }

      let inspectionPlan = plans.find(p => p.id === planId)
        ?? plans.find(p => p.inspectionTypeId === unstored[0].InspectionTypeId)
      if (!inspectionPlan) {
        inspectionPlan = await this.saveTemporaryInspectionPlanOffline(unstored, assetId, regionId, referenceDate)
      }

      const key = `inspections_inspectionPlan_${inspectionPlan.id}`
      const offlineInspections = (await this.offlineService.getItem(key))?.json as ElementResultInfo[] || []
      offlineInspections.push(...unstored)
      await this.offlineService.storeAsync(key, offlineInspections)
    }
    catch (error) {
      console.error(`Error saving inspection to offline !`, error)
    }
  }

  private async getSavedPlans(assetId: string, regionId: string): Promise<IInspectionPlan[]> {
    // Attention: more than one plan can be stored in inspectionPlan_assetId_regionId ; it is an array!
    const dbStoreData = await this.offlineService.getItem(`inspectionPlan_${assetId}_${regionId}`);
    if (!dbStoreData) {
      console.log(`Could not find inspectionPlan_${assetId}_${regionId} !`)
      await this.offlineService.storeAsync(`inspectionPlan_${assetId}_${regionId}`, [])
      console.log(`Created empty inspectionPlan_${assetId}_${regionId} !`)
      return []
    }
    return dbStoreData?.json as IInspectionPlan[] || []
  }

  async saveTemporaryInspectionPlanOffline(inspectionElements: ElementResultInfo[],
    assetId: string, regionId: string, referenceDate: Date
  ): Promise<IInspectionPlan> {
    const assetInfo = await this.assetService.getAssetInfobyId(assetId)

    const windowTimeInDays = assetInfo.AssetInfo.inspectionTimeScheduling * 30.44; // Average days in a month
    const halfWindowInDays = windowTimeInDays / 2;
    const halfWindowInMilliseconds = halfWindowInDays * 24 * 60 * 60 * 1000

    const assetWindowStartsAt = new Date(referenceDate.getTime() - halfWindowInMilliseconds)
    const assetWindowEndsAt = new Date(referenceDate.getTime() + halfWindowInMilliseconds)

    let inspectionPlan: IInspectionPlan = {
      id: uuid() as string,
      assetId: assetId,
      regionId: regionId,
      scheduleId: '',
      inspectionTypeId: inspectionElements[0].InspectionTypeId,
      plannedDate: new Date(referenceDate),
      startedAt: new Date(referenceDate),
      endedAt: new Date('0001-01-01T00:00:00Z'),
      assetWindowStartsAt: assetWindowStartsAt,
      assetWindowEndsAt: assetWindowEndsAt,
      outlinedElements: 0,
      skippedElements: 0,
      executedElements: 0,
      createdAt: new Date(),
      createdBy: '',
      updatedBy: '',
      inspectionTypeName: '',
      regionName: '',
      interval: 0,
      nextInspection: 0,
      displayNextInspectionValue: 0,
      dueDate: assetWindowEndsAt,
      reportDelivery: null,
      reportDeliveryLimit: null,
      lastCycleDate: null,
      inspectionCycle: 0,
      parentId: '',
      childId: '',
      dateCompleted: null,
      isCompleted: false,
    }

    let plansDbData = await this.offlineService.getItem(`inspectionPlan_${assetId}_${regionId}`);
    let plans = plansDbData?.json as IInspectionPlan[] || []
    plans.push(inspectionPlan)
    await this.offlineService.storeAsync(`inspectionPlan_${assetId}_${regionId}`, plans)

    return inspectionPlan
  }
}
